import React, { JSX, useEffect } from "react";
import { InputTypes } from "components/form/enumerations/input-types";
import { t } from "utilities/localization/t";
import { FormTextArea } from "components/form/form-textarea/form-textarea";
import { FormTextInput } from "components/form/form-input/form-text-input";
import { FormCalendarDatePicker } from "components/form/form-calendar-date-picker/form-calendar-date-picker";
import { ProviderFeeInvoiceRecord } from "models/view-models/providers/provider-fee-invoice-record";
import { toast } from "react-toastify";
import "./provider-invoice-form.scss";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface ProviderFeeInvoiceFormProps {
    providerFeeInvoice: ProviderFeeInvoiceRecord;
    onChange: (providerFeeInvoice: ProviderFeeInvoiceRecord) => void;
    isDirty: boolean;
    setIsDirty: (isDirty: boolean) => void;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME: string = "provider-invoice-form";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const ProviderFeeInvoiceForm: React.FC<ProviderFeeInvoiceFormProps> = ({ providerFeeInvoice, onChange, isDirty, setIsDirty }): JSX.Element => {
    
    const [invoiceAmountInput, setInvoiceAmountInput] = React.useState<string | undefined>(providerFeeInvoice?.invoiceAmount?.toFixed(2));

    const handleInvoiceTitleChange = (title?: string | undefined): void => {
        setIsDirty(true);
        onChange(providerFeeInvoice.with({ invoiceTitle: title ?? "" }));
    };

    const invoiceDate = providerFeeInvoice?.invoiceDate;
    const currentYear = invoiceDate != null
        ? new Date(invoiceDate).getFullYear()
        : new Date().getFullYear();

    const handleInvoiceDateChange = (date?: Date | undefined): void => {
        setIsDirty(true);
        onChange(providerFeeInvoice.with({ invoiceDate: date ?? new Date() }));
    };

    const handleInvoiceNotesBlur = (): void => {
        setIsDirty(true);
        onChange(providerFeeInvoice.with({ notes: providerFeeInvoice.notes }));
    };

    const handleInvoiceNotesChange = (notes?: string | undefined): void => {
        setIsDirty(true);
        onChange(providerFeeInvoice.with({ notes: notes ?? "" }));
    };

    
    const handleInvoiceAmountOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setInvoiceAmountInput(event.target.value);
        setIsDirty(true);
    };

    useEffect(() => {
        if (providerFeeInvoice?.id != null) {
            setInvoiceAmountInput(providerFeeInvoice?.invoiceAmount?.toFixed(2) ?? "");
            const parsedInvoiceAmount = invoiceAmountInput == null ? undefined : parseFloat(invoiceAmountInput);
            onChange(providerFeeInvoice.with({ invoiceAmount: parsedInvoiceAmount }));
        
        }
    }, [providerFeeInvoice?.invoiceAmount]);

    const updateInvoiceAmountOnBlur = async (value?: string | undefined): Promise<boolean> => {
        const numericInput = value?.replace(/[^0-9.]/g, "") ?? "";
        if (numericInput.length !== (value?.length ?? 0)) {
            toast.error(t("invalidCharactersRemovedFromInput"));
        }

        const parsedInvoiceAmount = parseFloat(numericInput);
        if (Number.isNaN(parsedInvoiceAmount)) {
            setInvoiceAmountInput("");
        } else {
            setInvoiceAmountInput(parsedInvoiceAmount.toFixed(2));
            onChange(providerFeeInvoice.with({ invoiceAmount: parsedInvoiceAmount }));
        }
        return true;
    };   

    

    return (
        <>
            <div>
              
                <div className={`${CSS_CLASS_NAME}__grid`}>
                    <FormTextInput
                        ariaLabelledBy={"invoiceTitle"}
                        autoFocus={false}
                        formFieldName="providerFeeInvoiceTitle"
                        id="ProviderFeeInvoiceTitle"
                        label={t("invoiceTitle")}
                        onChange={(e) => handleInvoiceTitleChange(e.target.value)}
                        placeholder={t("enterTitle")}
                        required={true}
                        type={InputTypes.Text}
                        value={providerFeeInvoice.invoiceTitle}
                        maxLength={60}
                    />
                    <div>
                        <FormTextInput
                            type={InputTypes.Text}
                            ariaLabelledBy={"invoiceAmount"}
                            formFieldName={"invoiceAmount"}
                            id={"invoiceAmount"}
                            label={t("invoiceAmount")}
                            minValue={0}
                            placeholder={t("invoiceAmount")}
                            required
                            value={invoiceAmountInput}
                            onChange={(e) => handleInvoiceAmountOnChange(e)}
                            onBlur={(e) => updateInvoiceAmountOnBlur(e.target.value)}
                        />
                    </div>
                    <div>
                        <FormCalendarDatePicker
                            cssClassName={`${CSS_CLASS_NAME}__form-calendar-date-picker`}
                            required
                            formFieldName={t("invoiceDate")}
                            label={t("invoiceDate")}
                            onChange={handleInvoiceDateChange}
                            placeholder={"emptyDate"}
                            selectedDate={providerFeeInvoice.invoiceDate}
                            minDate={new Date(currentYear, 0, 1)}
                            maxDate={new Date(currentYear, 11, 31)}
                        />
                    </div>
                    <div>
                        <FormTextArea
                            ariaLabelledBy={t("invoiceNotes")}
                            autoFocus={false}
                            formFieldName="providerFeeInvoiceNotes"
                            id="providerFeeInvoiceNotes"
                            rows={6}
                            label={t("invoiceNotes")}
                            onBlur={handleInvoiceNotesBlur}
                            onChange={(e) => handleInvoiceNotesChange(e.target.value)}
                            placeholder={t("enterNotes")}
                            required={false}
                            type={InputTypes.Text}
                            value={providerFeeInvoice.notes}
                            maxLength={200}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { ProviderFeeInvoiceForm };

// #endregion Exports
